import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { DOWNLOAD_URL } from './content';
import styles from './WebsitePage.module.scss';

interface VideoDialogProps {
  open: boolean;
  source: string;
  onClose: () => void;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video[controls]:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

export default function VideoDialog({ open, source, onClose }: VideoDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [mediaError, setMediaError] = useState(false);

  useEffect(() => {
    if (open) {
      setMediaError(false);
      videoRef.current?.load();
    }
  }, [open, source]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', closeOnEscape);

    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', closeOnEscape);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus();
    };
  }, [onClose, open]);

  if (!open) {
    return null;
  }

  const keepFocusInDialog = (event: ReactKeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== 'Tab' || !dialogRef.current) {
      return;
    }

    const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!first || !last) {
      event.preventDefault();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const closeFromBackdrop = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.currentTarget === event.target) {
      onClose();
    }
  };

  const retryVideo = () => {
    setMediaError(false);
    videoRef.current?.load();
  };

  return (
    <div className={styles.videoBackdrop} onMouseDown={closeFromBackdrop} role='presentation'>
      <dialog
        ref={dialogRef}
        open
        aria-labelledby='website-video-title'
        aria-modal='true'
        className={styles.videoDialog}
        data-testid='pc-video-dialog'
        onCancel={(event) => {
          event.preventDefault();
          onClose();
        }}
        onKeyDown={keepFocusInDialog}
      >
        <div className={styles.videoDialogHeader}>
          <h2 className={styles.srOnly} id='website-video-title'>
            脉脉介绍视频
          </h2>
          <button
            ref={closeButtonRef}
            type='button'
            className={styles.videoClose}
            data-testid='pc-video-close'
            onClick={onClose}
          >
            <span aria-hidden='true'>×</span>
            <span className={styles.srOnly}>关闭视频</span>
          </button>
        </div>
        <video
          ref={videoRef}
          className={`${styles.videoPlayer} ${mediaError ? styles.videoPlayerError : ''}`}
          controls
          controlsList='nodownload'
          autoPlay
          playsInline
          preload='metadata'
          data-testid='pc-video-player'
          aria-hidden={mediaError || undefined}
          tabIndex={mediaError ? -1 : undefined}
          onError={() => setMediaError(true)}
        >
          <source src={source} type='video/mp4' />
          您的浏览器暂不支持视频播放。
        </video>
        {mediaError && (
          <div className={styles.videoFallback} role='status' aria-live='polite' data-testid='pc-video-fallback'>
            <p>视频暂时无法加载，请重试或打开脉脉客户端观看。</p>
            <div className={styles.videoFallbackActions}>
              <button type='button' onClick={retryVideo} data-testid='pc-video-retry'>
                重新加载
              </button>
              <a href={DOWNLOAD_URL} target='_blank' rel='noopener noreferrer' data-testid='pc-video-fallback-link'>
                打开脉脉客户端
              </a>
            </div>
          </div>
        )}
      </dialog>
    </div>
  );
}
